import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import ModalFormaPgto from '~/Components/Vendas/ModalFormaPgto';
import {
  FORMAS_PAGAMENTO_BY_CODIGO,
  BOLETO,
  CHEQUE,
  CARTAO,
  DEPOSITO,
  DINHEIRO,
} from '~/constant';
import { formatCurrency, formatDate } from '../utils';

// Shape inicial do Formik do ModalFormaPgto. Espelha FORMA_PGTO_INICIAL de
// Components/Vendas/Sinal/index.js — todos os tipos precisam ter sub-objeto
// inicializado pra evitar acesso a undefined nos sub-componentes.
function makeFormaPgtoInicial() {
  var hoje = moment().format('DD/MM/YYYY');
  return {
    tipo_parcela: '',
    [BOLETO]: { qtdParcelas: '1', valor: '', vencimento: '', vencimento_mask: '' },
    [DINHEIRO]: { valor: '', vencimento: '', vencimento_mask: hoje },
    [DEPOSITO]: { valor: '', vencimento: '', vencimento_mask: hoje },
    [CARTAO]: {
      cartao: '',
      qtdParcelas: '1',
      valor: '',
      vencimento: '',
      vencimento_mask: hoje,
      numero_documento: '',
    },
    [CHEQUE]: {
      valor: '',
      vencimento: '',
      vencimento_mask: '',
      banco: '',
      agencia: '',
      digito2: '',
      camara_compensacao: '',
      numero: '',
      tipificacao: '',
      digito1: '',
      conta: '',
      digito3: '',
      cgccpf: '',
      emitente: '',
    },
  };
}

// Espelha inserirOuAtualizarFormaPagamento de Components/Vendas/Sinal/index.js:
// recebe values do Formik ({tipo_parcela, [tipo_parcela]: {...campos}}) e
// retorna versão flat ({tipo_parcela, ...campos}).
function flattenFormaPgto(values) {
  var tp = values.tipo_parcela;
  var sub = values[tp] || {};
  // Garantir vencimento como Date se houve preenchimento via mask
  var vencimento = sub.vencimento;
  if (!vencimento && sub.vencimento_mask) {
    var m = moment(sub.vencimento_mask, 'DD/MM/YYYY', true);
    vencimento = m.isValid() ? m.toDate() : null;
  }
  var qtd = sub.qtdParcelas ? parseInt(sub.qtdParcelas, 10) : 1;
  return Object.assign({}, sub, {
    tipo_parcela: tp,
    vencimento: vencimento,
    qtdParcelas: qtd,
  });
}

// Inverso de flattenFormaPgto — usado quando edita uma parcela existente.
// Reconstroi o shape nested que o Formik do ModalFormaPgto espera.
function nestFormaPgtoForEdit(p, index) {
  var inicial = makeFormaPgtoInicial();
  var tp = p.tipo_parcela;
  inicial.tipo_parcela = tp;
  // Copia todos os campos do flat de volta ao sub-objeto
  inicial[tp] = Object.assign({}, inicial[tp], p, {
    vencimento_mask: p.vencimento ? moment(p.vencimento).format('DD/MM/YYYY') : '',
    qtdParcelas: 1,
  });
  delete inicial[tp].tipo_parcela;
  inicial.index = index;
  return inicial;
}

export default function PagamentosSinalGravarVenda(props) {
  var parcelas = props.parcelas || [];
  var setParcelas = props.setParcelas;
  var totalRequerido = props.totalRequerido || 0;
  var cartoes = props.cartoes || [];
  var titulo = props.titulo || 'Pagamento do Sinal';
  var subtitulo = props.subtitulo || null;
  var readonly = !!props.readonly;

  var [showModal, setShowModal] = useState(false);
  var [edicaoIndex, setEdicaoIndex] = useState(null);
  var [edicaoValores, setEdicaoValores] = useState(null);

  var totalAdicionado = useMemo(function() {
    var soma = parcelas.reduce(function(acc, p) {
      return acc + (p.valor || 0);
    }, 0);
    return Math.round(soma * 100) / 100;
  }, [parcelas]);

  var totalFaltante = useMemo(function() {
    var f = totalRequerido - totalAdicionado;
    if (f <= 0.01) return 0;
    return Math.max(0, Math.round(f * 100) / 100);
  }, [totalRequerido, totalAdicionado]);

  var isCoberto = totalRequerido > 0 && totalFaltante === 0;
  var porcentagem = totalRequerido > 0
    ? (isCoberto ? 100 : Math.min(100, (totalAdicionado / totalRequerido) * 100))
    : 0;

  var handleAdicionarClick = useCallback(function() {
    setEdicaoIndex(null);
    setEdicaoValores(makeFormaPgtoInicial());
    setShowModal(true);
  }, []);

  var handleEditarClick = useCallback(function(index) {
    var p = parcelas[index];
    if (!p) return;
    setEdicaoIndex(index);
    setEdicaoValores(nestFormaPgtoForEdit(p, index));
    setShowModal(true);
  }, [parcelas]);

  var handleSave = useCallback(function(values) {
    var nova = flattenFormaPgto(values);
    var qtd = nova.qtdParcelas || 1;
    var lista;
    if (edicaoIndex !== null) {
      lista = parcelas.slice();
      lista[edicaoIndex] = nova;
    } else if (qtd > 1 && (nova.tipo_parcela === BOLETO || nova.tipo_parcela === CARTAO)) {
      // Espelha criaParcelamento do reducer real (Sinal/reducer.js)
      var novas = [];
      for (var i = 0; i < qtd; i++) {
        var venc = nova.vencimento ? moment(nova.vencimento).clone().add(i, 'months').toDate() : null;
        var clone = Object.assign({}, nova, { vencimento: venc, qtdParcelas: 1 });
        novas.push(clone);
      }
      lista = parcelas.concat(novas);
    } else {
      lista = parcelas.concat([nova]);
    }
    setParcelas(lista);
    setShowModal(false);
    setEdicaoIndex(null);
    setEdicaoValores(null);
  }, [parcelas, setParcelas, edicaoIndex]);

  var handleCancel = useCallback(function() {
    setShowModal(false);
    setEdicaoIndex(null);
    setEdicaoValores(null);
  }, []);

  var handleRemover = useCallback(function(index) {
    setParcelas(parcelas.filter(function(_, i) { return i !== index; }));
  }, [parcelas, setParcelas]);

  var handleLimpar = useCallback(function() {
    setParcelas([]);
  }, [setParcelas]);

  return (
    <Card className="mb-2 h-100" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <Card.Body className="py-2" style={{ overflow: 'hidden', minWidth: 0, maxWidth: '100%' }}>
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-2" style={{ gap: 4 }}>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h6 className="mb-0 text-primary text-truncate" title={titulo}><strong>{titulo}</strong></h6>
            {subtitulo && <small className="text-muted d-block text-truncate" title={subtitulo}>{subtitulo}</small>}
          </div>
          <div className="text-right" style={{ flexShrink: 0 }}>
            <small
              className="d-block"
              role="button"
              tabIndex={0}
              title="Clique para copiar o valor"
              style={{
                color: '#fd7e14',
                cursor: 'pointer',
                userSelect: 'none',
                borderBottom: '1px dashed #fd7e14',
                display: 'inline-block',
                paddingBottom: 1,
              }}
              onClick={() => {
                const texto = formatCurrency(totalRequerido);
                const fallback = () => {
                  const ta = document.createElement('textarea');
                  ta.value = texto;
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  try { document.execCommand('copy'); } catch (e) { /* noop */ }
                  document.body.removeChild(ta);
                };
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(texto)
                    .then(() => toast.success('Valor copiado: R$ ' + texto))
                    .catch(() => { fallback(); toast.success('Valor copiado: R$ ' + texto); });
                } else {
                  fallback();
                  toast.success('Valor copiado: R$ ' + texto);
                }
              }}
            >
              Total requerido: <strong>R$ {formatCurrency(totalRequerido)}</strong>
            </small>
            {totalAdicionado > 0 && (
              <small className={isCoberto ? 'text-success font-weight-bold' : 'text-warning'}>
                {isCoberto
                  ? '✓ Coberto'
                  : 'Faltam R$ ' + formatCurrency(totalFaltante)}
              </small>
            )}
          </div>
        </div>

        {totalRequerido > 0 && (
          <div className="mb-2">
            <div className="progress" style={{ height: 5 }}>
              <div
                className={'progress-bar ' + (isCoberto ? 'bg-success' : 'bg-warning')}
                style={{ width: porcentagem + '%', transition: 'width 0.3s' }}
              />
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: 6 }}>
          <Button
            size="sm"
            variant="primary"
            onClick={handleAdicionarClick}
            disabled={readonly}
            title="Adicionar forma de pagamento"
          >
            <FaPlus />
            <span className="d-none d-md-inline ml-1">Adicionar pagamento</span>
            <span className="d-inline d-md-none ml-1">Adicionar</span>
          </Button>
          {parcelas.length > 0 && !readonly && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleLimpar}
              title="Limpar todos os pagamentos"
            >
              <FaTrash />
              <span className="d-none d-md-inline ml-1">Limpar</span>
            </Button>
          )}
        </div>

        {parcelas.length === 0 ? (
          <Alert variant="light" className="py-1 mb-0 text-center">
            <small className="text-muted">
              Nenhum pagamento adicionado.
            </small>
          </Alert>
        ) : (
          <div style={{ width: '100%', overflow: 'hidden' }}>
            {parcelas.map(function(p, idx) {
              var info = FORMAS_PAGAMENTO_BY_CODIGO[p.tipo_parcela];
              var label = info ? info.label : ('Tipo ' + p.tipo_parcela);
              return (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: readonly ? '1fr' : '1fr auto',
                    gap: 8,
                    alignItems: 'center',
                    borderBottom: '1px solid #f5f5f5',
                    paddingTop: 4,
                    paddingBottom: 4,
                    width: '100%',
                  }}
                >
                  <div className="small" style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div className="d-flex align-items-center flex-wrap" style={{ rowGap: 2, columnGap: 6 }}>
                      <span className="badge badge-secondary">{label}</span>
                      <strong>R$ {formatCurrency(p.valor)}</strong>
                      <span className="text-muted">{formatDate(p.vencimento)}</span>
                    </div>
                    {(p.tipo_parcela === CHEQUE || p.tipo_parcela === CARTAO) && (
                      <div
                        className="text-muted text-truncate"
                        style={{ fontSize: '0.75rem', maxWidth: '100%' }}
                        title={(function() {
                          if (p.tipo_parcela === CHEQUE) {
                            var partes = [];
                            if (p.banco) partes.push('Bco ' + p.banco + ' · Ag ' + p.agencia + ' · Nº ' + p.numero);
                            if (p.emitente) partes.push(p.emitente);
                            return partes.join(' · ');
                          }
                          return p.numero_documento ? 'Cartão ' + p.numero_documento : '';
                        })()}
                      >
                        {p.tipo_parcela === CHEQUE && p.banco && (
                          <span>Bco {p.banco} · Ag {p.agencia} · Nº {p.numero}</span>
                        )}
                        {p.tipo_parcela === CHEQUE && p.emitente && (
                          <span className="ml-1">· {p.emitente}</span>
                        )}
                        {p.tipo_parcela === CARTAO && p.numero_documento && (
                          <span>Cartão {p.numero_documento}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {!readonly && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-1 text-secondary"
                        title="Editar"
                        onClick={function() { handleEditarClick(idx); }}
                      >
                        <FaPencilAlt />
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-1 text-danger"
                        title="Remover"
                        onClick={function() { handleRemover(idx); }}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>

      {showModal && edicaoValores && (
        <ModalFormaPgto
          show={showModal}
          onCancel={handleCancel}
          onSave={handleSave}
          initialValues={edicaoValores}
          cartoes={cartoes}
          isContraProposta={false}
        />
      )}
    </Card>
  );
}
